import type { ProjectData } from "grapesjs";

type StyleRule = {
  selectors?: unknown[];
  selectorsAdd?: string;
  state?: string;
  style?: Record<string, string>;
};

// Standard templates before NNVTN-941: #std-page outranks the id rule the Style Manager writes on a button
const OLD_LINK_RESET = "#std-page a";
const OLD_BUTTON = /^#std-page \.st-btn\.st-btn--(primary|light|outline-light)(:hover)?$/;
const OLD_TOGGLE = "#std-page .st-view-btn";
const OLD_TOGGLE_PRESSED = '#std-page .st-view-btn[aria-pressed="true"]';
const TOGGLE_PRESSED = '.st-page .st-view-btn[aria-pressed="true"]';

const sameValue = (a?: string, b?: string) =>
  a !== undefined && b !== undefined && a.trim().toLowerCase() === b.trim().toLowerCase();

const upgradeButton = (rule: StyleRule, variant: string, hover: boolean): StyleRule => {
  const { selectorsAdd: _old, ...rest } = rule;
  const style = { ...rule.style };
  // A border in the background's colour would stay behind as a ring once the editor recolours the button
  if (variant === "outline-light") {
    if (sameValue(style["border-color"], style.color)) style["border-color"] = "currentColor";
  } else if (sameValue(style["border-color"], style.background)) {
    delete style["border-color"];
  }
  return {
    ...rest,
    selectors: ["st-btn", `st-btn--${variant}`],
    style,
    ...(hover ? { state: "hover" } : {}),
  };
};

// The pressed toggle fills with the toggle's own colour, so a colour set on the toggle recolours both states
const upgradePressedToggle = (rule: StyleRule, toggleColour?: string): StyleRule[] => {
  const { color, ...style } = rule.style ?? {};
  if (!sameValue(style.background, toggleColour) || !sameValue(style["border-color"], toggleColour)) {
    return [{ ...rule, selectorsAdd: TOGGLE_PRESSED }];
  }
  return [
    {
      ...rule,
      selectorsAdd: TOGGLE_PRESSED,
      style: { ...style, background: "currentColor", "border-color": "currentColor" },
    },
    ...(color ? [{ selectors: [], selectorsAdd: `${TOGGLE_PRESSED} svg`, style: { color } }] : []),
  ];
};

const upgradeRule = (rule: StyleRule, toggleColour?: string): StyleRule[] => {
  switch (rule.selectorsAdd) {
    case OLD_LINK_RESET:
      return [{ ...rule, selectorsAdd: ".st-page a" }];
    case OLD_TOGGLE:
      return [{ ...rule, selectorsAdd: ".st-page .st-view-btn" }];
    case OLD_TOGGLE_PRESSED:
      return upgradePressedToggle(rule, toggleColour);
  }
  const match = OLD_BUTTON.exec(rule.selectorsAdd ?? "");
  return match ? [upgradeButton(rule, match[1], !!match[2])] : [rule];
};

export const upgradeStandardButtons = (data: ProjectData): ProjectData => {
  const styles = data.styles as StyleRule[] | undefined;
  if (!Array.isArray(styles)) return data;

  const toggleColour = styles.find((rule) => rule.selectorsAdd === OLD_TOGGLE)?.style?.color;
  let changed = false;
  const upgraded = styles.flatMap((rule) => {
    const next = upgradeRule(rule, toggleColour);
    if (next.length !== 1 || next[0] !== rule) changed = true;
    return next;
  });
  return changed ? { ...data, styles: upgraded } : data;
};
